import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowRight, Phone, Check, Thermometer,
  SunMedium, PanelsTopLeft, DoorOpen, Building2,
  Paintbrush, Layers, ChefHat, CloudLightning,
} from "lucide-react";
import SEO, { SITE_URL } from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { PageHero } from "./Services";
import { brand, heroImage, painting, remodeling } from "../data/site";

const icons = {
  SunMedium, PanelsTopLeft, DoorOpen, Building2,
  Paintbrush, Layers, ChefHat, CloudLightning,
};

const FAQ = [
  {
    q: "A roofer that paints?",
    a: "The same B General Residential classification that lets us remodel covers painting, and the work overlaps more than it sounds like it does. Fascia, eaves, parapet caps and the wall-to-roof transition are painted surfaces that fail for roofing reasons, and we are already up there. If a stain on your ceiling is coming back through the paint, the paint was never the problem.",
  },
  {
    q: "Why does prep cost more than the paint?",
    a: "Because it is what the job actually is. Material on an average Valley house is a small share of the price — the rest is washing, crack repair, patching, priming, caulking and masking. A bid that is meaningfully cheaper than the others is almost always a bid with less prep in it, and that difference shows up in year three rather than on the invoice.",
  },
  {
    q: "What paint do you use?",
    a: "Whatever the surface calls for, named by manufacturer, line and sheen on your estimate before you sign. On stucco that is usually a quality acrylic; on a wall with a lot of hairline cracking it may be an elastomeric, which bridges movement but is thicker and less breathable. We will tell you which one we are proposing and why, and if elastomeric is wrong for your wall we will say so.",
  },
  {
    q: "How long will an exterior last in Phoenix?",
    a: "A properly prepped acrylic on stucco generally runs eight to twelve years here, and the south and west elevations go first because they take the most ultraviolet. Anyone promising fifteen on a Valley exterior is selling the paint rather than the climate.",
  },
  {
    q: "Can you paint in summer?",
    a: painting.seasonNote,
  },
  {
    q: "What about HOA colors?",
    a: "We provide the manufacturer, line and color codes and the sample draw-downs your HOA submission needs. Getting the approval is yours; giving you what the form asks for is ours.",
  },
  {
    q: "Is the painting covered by a warranty?",
    a: `Painting carries a ${painting.warrantyYears}-year written workmanship warranty against peeling, blistering and delamination caused by how it was prepared or applied. That is separate from the ${remodeling.warrantyYears}-year remodeling warranty and from the roofing warranty. It does not cover normal fading, because nothing honest can — the Phoenix sun takes colour out of every coating made.`,
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      name: "Interior & Exterior Painting",
      serviceType: "Residential painting",
      provider: { "@type": "RoofingContractor", name: brand.name, url: SITE_URL },
      areaServed: { "@type": "AdministrativeArea", name: "Maricopa County, AZ" },
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Painting", item: `${SITE_URL}/painting` },
      ],
    },
  ],
};

// Hairline grid: container carries the rule colour, cells sit on card, the
// 1px gap between them becomes the divider and reflows at every breakpoint.
const Plate = ({ items }) => (
  <div className="rounded-2xl overflow-hidden border border-ink/10 bg-ink/10">
    <div className="grid sm:grid-cols-2 gap-px">
      {items.map((c) => {
        const Icon = icons[c.icon] || Paintbrush;
        return (
          <div key={c.title} className="bg-card flex gap-5 p-7 sm:p-8">
            <span className="w-10 h-10 rounded-lg bg-brand/10 flex items-center justify-center shrink-0 mt-0.5">
              <Icon size={18} className="text-brand-dark" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h3 className="font-heading text-lg uppercase tracking-wide mb-1.5">{c.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{c.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const Painting = () => {
  // Same licence gate as remodeling. Until the B classification is issued
  // there is no page here at all — offering the work is the violation under
  // A.R.S. § 32-1151, not just doing it.
  if (!painting.enabled) return <Navigate to="/services" replace />;

  return (
    <div>
      <SEO
        title="Interior & Exterior Painting in Phoenix, AZ | Pepla Roofing"
        description="Residential painting across Phoenix and Maricopa County — stucco repaints, fascia and trim, cabinets and drywall repair. Prep documented, products named in writing."
        jsonLd={jsonLd}
      />

      <PageHero
        eyebrow="Interior & Exterior"
        title="Painting"
        subtitle="The prep is the job and the paint is the easy part. We write down which one you are buying."
        image={heroImage}
        imageAlt="Phoenix home exterior"
      />

      {/* Exterior */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Exterior</h2>
          <div className="brand-line mb-5" />
          <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mb-10">
            Valley exteriors fail on a schedule — south and west first, fascia and trim before
            field walls, and always at the cracks nobody filled last time.
          </p>
          <Plate items={painting.exterior} />
        </div>
      </section>

      {/* Prep */}
      <section className="py-24 section-dark text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
              What You Are Actually Paying For
            </h2>
            <div className="brand-line mb-6" />
            <p className="text-white/70 leading-relaxed mb-5">
              Material is a small share of an exterior repaint. The rest is the list on the right,
              and it is where a cheap bid gets cheap. Every item on it appears on your estimate so
              you can hold us to it, and so you can ask the other bidder which ones they left out.
            </p>
            <p className="text-white/70 leading-relaxed">
              We photograph the cracks and the patched areas before anything is coated, the same
              way we photograph a deck before the underlayment covers it.
            </p>
          </div>
          <ul className="space-y-3 lg:pt-3">
            {painting.prep.map((t) => (
              <li key={t} className="flex items-start gap-3 text-white/85">
                <span className="w-6 h-6 rounded-full bg-brand/20 flex items-center justify-center shrink-0 mt-0.5">
                  <Check size={13} className="text-brand" aria-hidden="true" />
                </span>
                <span className="text-sm leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Interior */}
      <section className="py-24 bg-background">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">Interior</h2>
          <div className="brand-line mb-5" />
          <p className="text-muted-foreground text-lg leading-relaxed max-w-xl mb-10">
            Repairs matched to the texture that is already on your wall, and furniture treated
            like it belongs to someone.
          </p>
          <Plate items={painting.interior} />
        </div>
      </section>

      {/* Heat note — the thing that surprises people about a summer quote */}
      <section className="py-16 bg-secondary">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="flex gap-4">
            <Thermometer size={22} className="text-brand-dark shrink-0 mt-1" aria-hidden="true" />
            <div>
              <h2 className="font-heading text-2xl uppercase tracking-wide mb-2">
                Heat decides the schedule
              </h2>
              <p className="text-muted-foreground leading-relaxed">{painting.seasonNote}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-background">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <h2 className="font-heading text-4xl md:text-5xl uppercase tracking-tight mb-4">
            Questions People Ask
          </h2>
          <div className="brand-line mb-9" />
          <dl className="divide-y divide-ink/10">
            {FAQ.map((f) => (
              <div key={f.q} className="py-6">
                <dt className="font-heading text-lg uppercase tracking-wide mb-2">{f.q}</dt>
                <dd className="text-muted-foreground leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Estimate */}
      <section className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-4xl uppercase tracking-tight mb-4">Get It Priced</h2>
            <div className="brand-line mb-6" />
            <p className="text-muted-foreground leading-relaxed mb-6">
              We walk the elevations, count the cracks and the bad stucco, and give you a written
              scope with the products named and the prep itemised. It is yours whether or not you
              hire us.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Painting carries a <b className="text-ink">{painting.warrantyYears}-year written
              workmanship warranty</b> against peeling, blistering and delamination. Fading is not
              covered, here or anywhere, and we would rather say that now.
            </p>
            <a
              href={`tel:${brand.phoneRaw}`}
              className="inline-flex items-center gap-3 bg-brand hover:bg-brand-light text-ink font-semibold uppercase tracking-wider text-sm px-7 py-4 rounded-md transition-colors"
            >
              <Phone size={18} /> {brand.phone}
            </a>
            <p className="text-muted-foreground text-sm mt-8">
              Painting as part of a bigger job?{" "}
              <Link to="/remodeling" className="text-brand-dark font-semibold link-underline">
                Home remodeling <ArrowRight size={13} className="inline" />
              </Link>
            </p>
          </div>
          <div className="lg:col-span-3">
            <div className="bg-card border border-ink/10 rounded-2xl p-6 md:p-8 shadow-sm">
              <h3 className="font-heading text-2xl uppercase tracking-wide mb-2">Request An Estimate</h3>
              <p className="text-muted-foreground text-sm mb-6">
                Tell us what needs painting and a team member will call to arrange a walk-through.
              </p>
              <LeadForm defaultService="Painting" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Painting;
